import React from "react";
import {
  Card,
  Descriptions,
  Tag,
  Typography,
  Space,
  Button,
  Collapse,
  Badge,
} from "antd";
import {
  EditOutlined,
  UserOutlined,
  IdcardOutlined,
  FileProtectOutlined,
  BankOutlined,
  GlobalOutlined,
  SafetyOutlined,
  DollarOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { FixedIncomeRequest } from "../helpers/shared-interfaces";
import { formatSnakeCase, capitalizeFirstLetter } from "../helpers/utilities";
import moment from "moment";
import { tabPaneStyle, amountRangesOptions } from "../helpers/constants";
import { RiskProfileDisplay } from "./RiskProfileDisplay";

const { Text } = Typography;
const { Panel } = Collapse;

interface Props {
  fixedIncomeRequest?: FixedIncomeRequest;
  onEdit?: () => void;
}

export const FixedIncomeRequestKycView: React.FC<Props> = ({
  fixedIncomeRequest,
  onEdit,
}) => {
  const kyc = fixedIncomeRequest?.fixedIncomeRequestKyc;
  if (!kyc) {
    return null;
  }

  const canEdit = ![
    "approved",
    "pending_payment_verification",
    "payment_verified",
    "purchased",
  ].includes(fixedIncomeRequest?.state || "");

  const formatBoolean = (value: boolean | null | undefined): string => {
    if (value === null || value === undefined) return "N/A";
    return value ? "Yes" : "No";
  };

  return (
    <div style={{ marginTop: 24 }}>
      {fixedIncomeRequest?.state !== "pending_kyc" && kyc?.riskProfile && (
        <RiskProfileDisplay riskProfile={kyc.riskProfile} />
      )}
      <Collapse style={{ marginTop: 16 }}>
        <Panel
          header={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>
                KYC Information <Badge count={1} style={tabPaneStyle} />
              </span>
              {canEdit && onEdit && (
                <Button
                  className="base-button"
                  size="middle"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  Edit
                </Button>
              )}
            </div>
          }
          key="kyc"
        >
          {/* Personal Information Section */}
          <Card
            type="inner"
            title={
              <Space>
                <UserOutlined />
                <Text strong>Personal Information</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Descriptions column={3} size="small">
              {kyc.title && (
                <Descriptions.Item label="Title">
                  {formatSnakeCase(capitalizeFirstLetter(kyc.title))}
                </Descriptions.Item>
              )}
              {kyc.surname && (
                <Descriptions.Item label="Surname">
                  {kyc.surname}
                </Descriptions.Item>
              )}
              {kyc.firstName && (
                <Descriptions.Item label="First Name">
                  {kyc.firstName}
                </Descriptions.Item>
              )}
              {kyc.otherNames && (
                <Descriptions.Item label="Other Names">
                  {kyc.otherNames}
                </Descriptions.Item>
              )}
              {kyc.gender && (
                <Descriptions.Item label="Gender">
                  {formatSnakeCase(capitalizeFirstLetter(kyc.gender))}
                </Descriptions.Item>
              )}
              {kyc.maritalStatus && (
                <Descriptions.Item label="Marital Status">
                  {formatSnakeCase(capitalizeFirstLetter(kyc.maritalStatus))}
                </Descriptions.Item>
              )}
              {kyc.dateOfBirth && (
                <Descriptions.Item label="Date of Birth">
                  {moment(kyc.dateOfBirth, "YYYY-MM-DD").format("DD/MM/YYYY")}
                </Descriptions.Item>
              )}
              {kyc.placeOfBirth && (
                <Descriptions.Item label="Place of Birth">
                  {kyc.placeOfBirth}
                </Descriptions.Item>
              )}
              {kyc.mothersMaidenName && (
                <Descriptions.Item label="Mother's Maiden Name">
                  {kyc.mothersMaidenName}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Contact Information Section */}
          <Card
            type="inner"
            title={
              <Space>
                <EnvironmentOutlined />
                <Text strong>Contact Information</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Descriptions column={3} size="small">
              {kyc.residentialStatus && (
                <Descriptions.Item label="Residential Status">
                  {formatSnakeCase(
                    capitalizeFirstLetter(kyc.residentialStatus),
                  )}
                </Descriptions.Item>
              )}
              {kyc.countryOfOrigin && (
                <Descriptions.Item label="Country of Origin">
                  {kyc.countryOfOrigin}
                </Descriptions.Item>
              )}
              {kyc.countryOfResidence && (
                <Descriptions.Item label="Country of Residence">
                  {kyc.countryOfResidence}
                </Descriptions.Item>
              )}
              {kyc.residentRegion && (
                <Descriptions.Item label="Region">
                  {capitalizeFirstLetter(formatSnakeCase(kyc.residentRegion))}
                </Descriptions.Item>
              )}
              {kyc.residentCity && (
                <Descriptions.Item label="City">
                  {kyc.residentCity}
                </Descriptions.Item>
              )}
              {kyc.residentPermitStatus && (
                <Descriptions.Item label="Resident Permit Status">
                  {formatSnakeCase(
                    capitalizeFirstLetter(kyc.residentPermitStatus),
                  )}
                </Descriptions.Item>
              )}
              {kyc.residentialAddress && (
                <Descriptions.Item label="Residential Address" span={3}>
                  {kyc.residentialAddress}
                </Descriptions.Item>
              )}
              {kyc.digitalAddress && (
                <Descriptions.Item label="Digital Address">
                  {kyc.digitalAddress}
                </Descriptions.Item>
              )}
              {kyc.mobileNumber && (
                <Descriptions.Item label="Mobile Number">
                  {kyc.mobileNumber}
                </Descriptions.Item>
              )}
              {kyc.emailAddress && (
                <Descriptions.Item label="Email Address">
                  {kyc.emailAddress}
                </Descriptions.Item>
              )}
              {kyc.residentPermitNumber && (
                <Descriptions.Item label="Resident Permit Number">
                  {kyc.residentPermitNumber}
                </Descriptions.Item>
              )}
              {kyc.placeOfIssue && (
                <Descriptions.Item label="Place of Issue">
                  {kyc.placeOfIssue}
                </Descriptions.Item>
              )}
              {kyc.permitIssueDate && (
                <Descriptions.Item label="Permit Issue Date">
                  {moment(kyc.permitIssueDate, "YYYY-MM-DD").format(
                    "DD/MM/YYYY",
                  )}
                </Descriptions.Item>
              )}
              {kyc.permitExpiryDate && (
                <Descriptions.Item label="Permit Expiry Date">
                  {moment(kyc.permitExpiryDate, "YYYY-MM-DD").format(
                    "DD/MM/YYYY",
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Identification Section */}
          <Card
            type="inner"
            title={
              <Space>
                <IdcardOutlined />
                <Text strong>Identification</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Descriptions column={3} size="small">
              {fixedIncomeRequest?.customer?.ghanaCardNo && (
                <Descriptions.Item label="Ghana Card Number">
                  {fixedIncomeRequest.customer.ghanaCardNo}
                </Descriptions.Item>
              )}
              {fixedIncomeRequest?.customer?.ghanaCardIdUrl && (
                <Descriptions.Item label="Ghana Card (Front)">
                  <a
                    href={fixedIncomeRequest.customer.ghanaCardIdUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Front
                  </a>
                </Descriptions.Item>
              )}
              {fixedIncomeRequest?.customer?.ghanaCardIdBackUrl && (
                <Descriptions.Item label="Ghana Card (Back)">
                  <a
                    href={fixedIncomeRequest.customer.ghanaCardIdBackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Back
                  </a>
                </Descriptions.Item>
              )}
              {fixedIncomeRequest?.customer?.idIssueDate && (
                <Descriptions.Item label="Ghana Card Issue Date">
                  {moment(
                    fixedIncomeRequest.customer.idIssueDate,
                    "YYYY-MM-DD",
                  ).format("DD/MM/YYYY")}
                </Descriptions.Item>
              )}
              {fixedIncomeRequest?.customer?.idExpiryDate && (
                <Descriptions.Item label="Ghana Card Expiry Date">
                  {moment(
                    fixedIncomeRequest.customer.idExpiryDate,
                    "YYYY-MM-DD",
                  ).format("DD/MM/YYYY")}
                </Descriptions.Item>
              )}
              {kyc.taxIdentificationNumber && (
                <Descriptions.Item label="TIN">
                  {kyc.taxIdentificationNumber}
                </Descriptions.Item>
              )}
              {kyc.passportUrl && (
                <Descriptions.Item label="Passport">
                  <a
                    href={kyc.passportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Passport
                  </a>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Employment Details Section */}
          <Card
            type="inner"
            title={
              <Space>
                <FileProtectOutlined />
                <Text strong>Employment Details</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Descriptions column={3} size="small">
              {kyc.employmentStatus && (
                <Descriptions.Item label="Employment Status">
                  {formatSnakeCase(capitalizeFirstLetter(kyc.employmentStatus))}
                </Descriptions.Item>
              )}
              {kyc.nameOfEmployerSchool && (
                <Descriptions.Item label="Employer/School Name">
                  {kyc.nameOfEmployerSchool}
                </Descriptions.Item>
              )}
              {kyc.addressOfEmployerSchool && (
                <Descriptions.Item label="Employer/School Address">
                  {kyc.addressOfEmployerSchool}
                </Descriptions.Item>
              )}
              {kyc.cityTown && (
                <Descriptions.Item label="City/Town">
                  {kyc.cityTown}
                </Descriptions.Item>
              )}
              {kyc.natureOfBusiness && (
                <Descriptions.Item label="Nature of Business">
                  {kyc.natureOfBusiness}
                </Descriptions.Item>
              )}
              {kyc.businessSchoolOfficeContactNumber && (
                <Descriptions.Item label="Office Contact">
                  {kyc.businessSchoolOfficeContactNumber}
                </Descriptions.Item>
              )}
              {kyc.businessSchoolOfficeEmail && (
                <Descriptions.Item label="Office Email">
                  {kyc.businessSchoolOfficeEmail}
                </Descriptions.Item>
              )}
              {kyc.yearsOfEmployment !== null &&
                kyc.yearsOfEmployment !== undefined && (
                  <Descriptions.Item label="Total Years of Employment">
                    {kyc.yearsOfEmployment} years
                  </Descriptions.Item>
                )}
              {kyc.yearsOfCurrentEmployment !== null &&
                kyc.yearsOfCurrentEmployment !== undefined && (
                  <Descriptions.Item label="Years at Current Employment">
                    {kyc.yearsOfCurrentEmployment} years
                  </Descriptions.Item>
                )}
              {kyc.occupation && (
                <Descriptions.Item label="Occupation">
                  {kyc.occupation}
                </Descriptions.Item>
              )}
              {kyc.taxIdentificationNumber && (
                <Descriptions.Item label="TIN">
                  {kyc.taxIdentificationNumber}
                </Descriptions.Item>
              )}
              {kyc.profession && (
                <Descriptions.Item label="Profession">
                  {kyc.profession}
                </Descriptions.Item>
              )}
              {kyc.professionalLicenseNumber && (
                <Descriptions.Item label="Professional License Number">
                  {kyc.professionalLicenseNumber}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Payout Account Information Section */}
          {(kyc.payoutAccountName ||
            kyc.payoutAccountNumber ||
            kyc.payoutBankName ||
            kyc.payoutBankBranchName ||
            kyc.payoutPaymentMethod) && (
            <Card
              type="inner"
              title={
                <Space>
                  <BankOutlined />
                  <Text strong>Payout Account Information</Text>
                </Space>
              }
              style={{ marginBottom: 16 }}
              size="small"
            >
              <Descriptions column={3} size="small">
                {kyc.payoutCurrency && (
                  <Descriptions.Item label="Payout Currency">
                    {capitalizeFirstLetter(kyc.payoutCurrency)}
                  </Descriptions.Item>
                )}
                {kyc.payoutPaymentMethod && (
                  <Descriptions.Item label="Payment Method">
                    {formatSnakeCase(
                      capitalizeFirstLetter(kyc.payoutPaymentMethod),
                    )}
                  </Descriptions.Item>
                )}
                {kyc.payoutBankName && (
                  <Descriptions.Item label="Bank/Provider Name">
                    {kyc.payoutBankName}
                  </Descriptions.Item>
                )}
                {kyc.payoutBankBranchName && (
                  <Descriptions.Item label="Branch Name">
                    {kyc.payoutBankBranchName}
                  </Descriptions.Item>
                )}
                {kyc.payoutAccountName && (
                  <Descriptions.Item label="Account Name">
                    {kyc.payoutAccountName}
                  </Descriptions.Item>
                )}
                {kyc.payoutAccountNumber && (
                  <Descriptions.Item label="Account Number">
                    {kyc.payoutAccountNumber}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Beneficiary Information Section */}
          {(kyc.beneficiaries?.length || kyc.beneficiaryType) && (
            <Card
              type="inner"
              title={
                <Space>
                  <TeamOutlined />
                  <Text strong>Beneficiary Information</Text>
                </Space>
              }
              style={{ marginBottom: 16 }}
              size="small"
            >
              {kyc.beneficiaryType && (
                <Descriptions
                  column={3}
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions.Item label="Beneficiary Type" span={3}>
                    {formatSnakeCase(
                      capitalizeFirstLetter(kyc.beneficiaryType),
                    )}
                  </Descriptions.Item>
                </Descriptions>
              )}

              {kyc.beneficiaries &&
                kyc.beneficiaries.length > 0 &&
                kyc.beneficiaries.map((beneficiary, index) => (
                  <Card
                    key={index}
                    type="inner"
                    size="small"
                    title={
                      <Space>
                        <Text strong>
                          {beneficiary.beneficiaryFullName ||
                            `${
                              kyc.beneficiaryType === "beneficiary"
                                ? "Beneficiary"
                                : "Person"
                            } ${index + 1}`}
                        </Text>
                        {kyc.beneficiaryType === "beneficiary" &&
                          beneficiary.beneficiaryPercentageOfBenefit && (
                            <Tag color="blue">
                              {beneficiary.beneficiaryPercentageOfBenefit}%
                            </Tag>
                          )}
                      </Space>
                    }
                    style={{
                      marginBottom:
                        index < kyc.beneficiaries.length - 1 ? 16 : 0,
                    }}
                  >
                    <Descriptions column={3} size="small">
                      {beneficiary.beneficiaryTitle && (
                        <Descriptions.Item label="Title">
                          {formatSnakeCase(
                            capitalizeFirstLetter(beneficiary.beneficiaryTitle),
                          )}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryFullName && (
                        <Descriptions.Item label="Full Name">
                          {beneficiary.beneficiaryFullName}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryGender && (
                        <Descriptions.Item label="Gender">
                          {formatSnakeCase(
                            capitalizeFirstLetter(
                              beneficiary.beneficiaryGender,
                            ),
                          )}
                        </Descriptions.Item>
                      )}
                      {kyc.beneficiaryType === "beneficiary" &&
                        beneficiary.beneficiaryPercentageOfBenefit !== null &&
                        beneficiary.beneficiaryPercentageOfBenefit !==
                          undefined && (
                          <Descriptions.Item label="Percentage of Benefit">
                            {beneficiary.beneficiaryPercentageOfBenefit}%
                          </Descriptions.Item>
                        )}
                      {beneficiary.beneficiaryRelationship && (
                        <Descriptions.Item label="Relationship">
                          {formatSnakeCase(
                            capitalizeFirstLetter(
                              beneficiary.beneficiaryRelationship,
                            ),
                          )}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryMaritalStatus && (
                        <Descriptions.Item label="Marital Status">
                          {formatSnakeCase(
                            capitalizeFirstLetter(
                              beneficiary.beneficiaryMaritalStatus,
                            ),
                          )}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryDateOfBirth && (
                        <Descriptions.Item label="Date of Birth">
                          {moment(
                            beneficiary.beneficiaryDateOfBirth,
                            "YYYY-MM-DD",
                          ).format("DD/MM/YYYY")}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryPlaceOfBirth && (
                        <Descriptions.Item label="Place of Birth">
                          {beneficiary.beneficiaryPlaceOfBirth}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryResidentialAddress && (
                        <Descriptions.Item label="Residential Address" span={3}>
                          {beneficiary.beneficiaryResidentialAddress}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryDigitalAddress && (
                        <Descriptions.Item label="Digital Address">
                          {beneficiary.beneficiaryDigitalAddress}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryOccupation && (
                        <Descriptions.Item label="Occupation">
                          {beneficiary.beneficiaryOccupation}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryTaxIdentificationNumber && (
                        <Descriptions.Item label="TIN">
                          {beneficiary.beneficiaryTaxIdentificationNumber}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryMobileNumber1 && (
                        <Descriptions.Item label="Mobile Number 1">
                          {beneficiary.beneficiaryMobileNumber1}
                        </Descriptions.Item>
                      )}
                      {beneficiary.beneficiaryMobileNumber2 && (
                        <Descriptions.Item label="Mobile Number 2">
                          {beneficiary.beneficiaryMobileNumber2}
                        </Descriptions.Item>
                      )}
                      {kyc.beneficiaryType === "in_trust_for" &&
                        beneficiary.beneficiaryGhanaCardUrl && (
                          <Descriptions.Item label="Ghana Card">
                            <a
                              href={beneficiary.beneficiaryGhanaCardUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Document
                            </a>
                          </Descriptions.Item>
                        )}
                      {kyc.beneficiaryType === "in_trust_for" &&
                        beneficiary.beneficiaryPassportUrl && (
                          <Descriptions.Item label="Passport">
                            <a
                              href={beneficiary.beneficiaryPassportUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Document
                            </a>
                          </Descriptions.Item>
                        )}
                    </Descriptions>
                  </Card>
                ))}
            </Card>
          )}

          {/* Financial Information Section */}
          {(kyc.sourceOfFunds ||
            kyc.expectedDepositAmount ||
            kyc.expectedWithdrawalAmount ||
            kyc.expectedFrequencyOfDeposit ||
            kyc.expectedFrequencyOfWithdrawal) && (
            <Card
              type="inner"
              title={
                <Space>
                  <DollarOutlined />
                  <Text strong>Financial Information</Text>
                </Space>
              }
              style={{ marginBottom: 16 }}
              size="small"
            >
              <Descriptions column={3} size="small">
                {kyc.sourceOfFunds && (
                  <Descriptions.Item label="Source of Funds">
                    {formatSnakeCase(capitalizeFirstLetter(kyc.sourceOfFunds))}
                  </Descriptions.Item>
                )}
                {kyc.expectedDepositAmount && (
                  <Descriptions.Item label="Expected Deposit Amount">
                    {amountRangesOptions.find(
                      (opt) => opt.value === kyc.expectedDepositAmount,
                    )?.name ??
                      formatSnakeCase(
                        capitalizeFirstLetter(kyc.expectedDepositAmount),
                      )}
                  </Descriptions.Item>
                )}
                {kyc.expectedWithdrawalAmount && (
                  <Descriptions.Item label="Expected Withdrawal Amount">
                    {amountRangesOptions.find(
                      (opt) => opt.value === kyc.expectedWithdrawalAmount,
                    )?.name ??
                      formatSnakeCase(
                        capitalizeFirstLetter(kyc.expectedWithdrawalAmount),
                      )}
                  </Descriptions.Item>
                )}
                {kyc.expectedFrequencyOfDeposit && (
                  <Descriptions.Item label="Expected Frequency of Deposit">
                    {formatSnakeCase(
                      capitalizeFirstLetter(kyc.expectedFrequencyOfDeposit),
                    )}
                  </Descriptions.Item>
                )}
                {kyc.expectedFrequencyOfWithdrawal && (
                  <Descriptions.Item label="Expected Frequency of Withdrawal">
                    {formatSnakeCase(
                      capitalizeFirstLetter(kyc.expectedFrequencyOfWithdrawal),
                    )}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Foreign Status Section */}
          {(kyc.isCitizenOfForeignCountry ||
            kyc.holdsPassportOfForeignCountry ||
            kyc.holdsGreenCardOfForeignCountry ||
            kyc.isResidentInForeignCountry ||
            kyc.spentMoreThan183DaysInForeignCountry ||
            kyc.foreignFullName ||
            kyc.foreignResidentialAddress ||
            kyc.foreignMailingAddress ||
            kyc.foreignTelephoneNumber ||
            kyc.foreignTinSsnNationalId) && (
            <Card
              type="inner"
              title={
                <Space>
                  <GlobalOutlined />
                  <Text strong>Foreign Status</Text>
                </Space>
              }
              style={{ marginBottom: 16 }}
              size="small"
            >
              <Descriptions column={3} size="small">
                {kyc.isCitizenOfForeignCountry !== null &&
                  kyc.isCitizenOfForeignCountry !== undefined && (
                    <Descriptions.Item label="Citizen of Foreign Country">
                      <Tag
                        color={
                          kyc.isCitizenOfForeignCountry ? "orange" : "green"
                        }
                      >
                        {formatBoolean(kyc.isCitizenOfForeignCountry)}
                      </Tag>
                    </Descriptions.Item>
                  )}
                {kyc.holdsPassportOfForeignCountry !== null &&
                  kyc.holdsPassportOfForeignCountry !== undefined && (
                    <Descriptions.Item label="Holds Foreign Passport">
                      <Tag
                        color={
                          kyc.holdsPassportOfForeignCountry ? "orange" : "green"
                        }
                      >
                        {formatBoolean(kyc.holdsPassportOfForeignCountry)}
                      </Tag>
                    </Descriptions.Item>
                  )}
                {kyc.holdsGreenCardOfForeignCountry !== null &&
                  kyc.holdsGreenCardOfForeignCountry !== undefined && (
                    <Descriptions.Item label="Holds Green Card">
                      <Tag
                        color={
                          kyc.holdsGreenCardOfForeignCountry
                            ? "orange"
                            : "green"
                        }
                      >
                        {formatBoolean(kyc.holdsGreenCardOfForeignCountry)}
                      </Tag>
                    </Descriptions.Item>
                  )}
                {kyc.isResidentInForeignCountry !== null &&
                  kyc.isResidentInForeignCountry !== undefined && (
                    <Descriptions.Item label="Resident in Foreign Country">
                      <Tag
                        color={
                          kyc.isResidentInForeignCountry ? "orange" : "green"
                        }
                      >
                        {formatBoolean(kyc.isResidentInForeignCountry)}
                      </Tag>
                    </Descriptions.Item>
                  )}
                {kyc.spentMoreThan183DaysInForeignCountry !== null &&
                  kyc.spentMoreThan183DaysInForeignCountry !== undefined && (
                    <Descriptions.Item label="Spent 183+ Days Abroad">
                      <Tag
                        color={
                          kyc.spentMoreThan183DaysInForeignCountry
                            ? "orange"
                            : "green"
                        }
                      >
                        {formatBoolean(
                          kyc.spentMoreThan183DaysInForeignCountry,
                        )}
                      </Tag>
                    </Descriptions.Item>
                  )}
                {kyc.foreignFullName && (
                  <Descriptions.Item label="Foreign Full Name">
                    {kyc.foreignFullName}
                  </Descriptions.Item>
                )}
                {kyc.foreignResidentialAddress && (
                  <Descriptions.Item
                    label="Foreign Residential Address"
                    span={3}
                  >
                    {kyc.foreignResidentialAddress}
                  </Descriptions.Item>
                )}
                {kyc.foreignMailingAddress && (
                  <Descriptions.Item label="Foreign Mailing Address" span={3}>
                    {kyc.foreignMailingAddress}
                  </Descriptions.Item>
                )}
                {kyc.foreignTelephoneNumber && (
                  <Descriptions.Item label="Foreign Telephone">
                    {kyc.foreignTelephoneNumber}
                  </Descriptions.Item>
                )}
                {kyc.foreignTinSsnNationalId && (
                  <Descriptions.Item label="Foreign TIN/SSN/National ID">
                    {kyc.foreignTinSsnNationalId}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Investment Profile Section */}
          <Card
            type="inner"
            title={
              <Space>
                <BankOutlined />
                <Text strong>Investment Profile</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Descriptions column={3} size="small">
              {kyc.investmentCurrency && (
                <Descriptions.Item label="Investment Currency">
                  {formatSnakeCase(
                    capitalizeFirstLetter(kyc.investmentCurrency),
                  )}
                </Descriptions.Item>
              )}
              {kyc.approximateAnnualIncome && (
                <Descriptions.Item label="Approximate Annual Income">
                  {formatSnakeCase(
                    capitalizeFirstLetter(kyc.approximateAnnualIncome),
                  )}
                </Descriptions.Item>
              )}
              {kyc.investmentHorizon && (
                <Descriptions.Item label="Investment Horizon">
                  {formatSnakeCase(
                    capitalizeFirstLetter(kyc.investmentHorizon),
                  )}
                </Descriptions.Item>
              )}
              {kyc.investmentKnowledge && (
                <Descriptions.Item label="Investment Knowledge">
                  {formatSnakeCase(
                    capitalizeFirstLetter(kyc.investmentKnowledge),
                  )}
                </Descriptions.Item>
              )}
              {kyc.riskTolerance && (
                <Descriptions.Item label="Risk Tolerance">
                  {formatSnakeCase(capitalizeFirstLetter(kyc.riskTolerance))}
                </Descriptions.Item>
              )}
              {kyc.communicationMethod && (
                <Descriptions.Item label="Communication Method">
                  {Array.isArray(kyc.communicationMethod)
                    ? kyc.communicationMethod
                        .map((method) =>
                          formatSnakeCase(capitalizeFirstLetter(method)),
                        )
                        .join(", ")
                    : formatSnakeCase(
                        capitalizeFirstLetter(kyc.communicationMethod),
                      )}
                </Descriptions.Item>
              )}
              {kyc.objectives && (
                <Descriptions.Item label="Investment Objectives" span={3}>
                  {kyc.objectives}
                </Descriptions.Item>
              )}
              {kyc.statementFrequency && (
                <Descriptions.Item label="Statement Frequency" span={3}>
                  {kyc.statementFrequency}
                </Descriptions.Item>
              )}
              {kyc.statementFrequencyOther && (
                <Descriptions.Item label="Statement Frequency (Other)" span={3}>
                  {kyc.statementFrequencyOther}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Securities Information Section */}
          <Card
            type="inner"
            title={
              <Space>
                <SafetyOutlined />
                <Text strong>Securities Information</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Descriptions column={3} size="small">
              {kyc.hasBoughtSecuritiesBefore !== null &&
                kyc.hasBoughtSecuritiesBefore !== undefined && (
                  <Descriptions.Item label="Bought Securities Before">
                    <Tag
                      color={kyc.hasBoughtSecuritiesBefore ? "blue" : "default"}
                    >
                      {formatBoolean(kyc.hasBoughtSecuritiesBefore)}
                    </Tag>
                  </Descriptions.Item>
                )}
              {kyc.existingCsdClientId && (
                <Descriptions.Item label="Existing CSD Client ID">
                  {kyc.existingCsdClientId}
                </Descriptions.Item>
              )}
              {kyc.clientType && (
                <Descriptions.Item label="Client Type">
                  {formatSnakeCase(capitalizeFirstLetter(kyc.clientType))}
                </Descriptions.Item>
              )}
              {kyc.clientCsdSecuritiesAccountNumbers && (
                <Descriptions.Item
                  label="CSD Securities Account Numbers"
                  span={3}
                >
                  {kyc.clientCsdSecuritiesAccountNumbers}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Compliance Information Section */}
          {(kyc.accountOrigination || kyc.pepStatus) && (
            <Card
              type="inner"
              title={
                <Space>
                  <SafetyCertificateOutlined />
                  <Text strong>Compliance Information</Text>
                </Space>
              }
              style={{ marginBottom: 16 }}
              size="small"
            >
              <Descriptions column={3} size="small">
                {kyc.accountOrigination && (
                  <Descriptions.Item label="Account Origination">
                    {formatSnakeCase(
                      capitalizeFirstLetter(kyc.accountOrigination),
                    )}
                  </Descriptions.Item>
                )}
                {kyc.pepStatus && (
                  <Descriptions.Item label="PEP Status">
                    <Tag color={kyc.pepStatus === "yes" ? "red" : "green"}>
                      {formatSnakeCase(capitalizeFirstLetter(kyc.pepStatus))}
                    </Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Declaration & Documents Section */}
          {((kyc.kycDeclaration !== null && kyc.kycDeclaration !== undefined) ||
            (kyc.kycDocuments && kyc.kycDocuments.length > 0)) && (
            <Card
              type="inner"
              title={
                <Space>
                  <FileProtectOutlined />
                  <Text strong>Declaration & Documents</Text>
                </Space>
              }
              style={{ marginBottom: 16 }}
              size="small"
            >
              <Descriptions column={3} size="small">
                {kyc.kycDeclaration !== null &&
                  kyc.kycDeclaration !== undefined && (
                    <Descriptions.Item label="KYC Declaration">
                      <Tag color={kyc.kycDeclaration ? "green" : "red"}>
                        {kyc.kycDeclaration ? "Yes" : "No"}
                      </Tag>
                    </Descriptions.Item>
                  )}
                {kyc.kycDocuments && kyc.kycDocuments.length > 0 && (
                  <Descriptions.Item label="KYC Documents" span={3}>
                    <Space direction="vertical">
                      {kyc.kycDocuments.map((doc, index) => {
                        const url = doc.split("@@@")[0];
                        const name =
                          doc.split("@@@")[1] || `Document ${index + 1}`;
                        return (
                          <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {name}
                          </a>
                        );
                      })}
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* CSD Verification Section */}
          {(kyc.verifiedByCsdOfficer ||
            kyc.csdOfficerSignatureUrl ||
            kyc.csdVerificationDate ||
            kyc.csdStampUrl) && (
            <Card
              type="inner"
              title={
                <Space>
                  <SafetyOutlined />
                  <Text strong>CSD Verification</Text>
                </Space>
              }
              size="small"
            >
              <Descriptions column={3} size="small">
                {kyc.verifiedByCsdOfficer && (
                  <Descriptions.Item label="Verified By">
                    {kyc.verifiedByCsdOfficer}
                  </Descriptions.Item>
                )}
                {kyc.csdVerificationDate && (
                  <Descriptions.Item label="Verification Date">
                    {moment(kyc.csdVerificationDate, "YYYY-MM-DD").format(
                      "DD/MM/YYYY",
                    )}
                  </Descriptions.Item>
                )}
                {kyc.csdOfficerSignatureUrl && (
                  <Descriptions.Item label="Officer Signature">
                    <a
                      href={kyc.csdOfficerSignatureUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Signature
                    </a>
                  </Descriptions.Item>
                )}
                {kyc.csdStampUrl && (
                  <Descriptions.Item label="CSD Stamp">
                    <a
                      href={kyc.csdStampUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Stamp
                    </a>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}
        </Panel>
      </Collapse>
    </div>
  );
};
